import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { Fragment, type ReactNode } from 'react';

import { Button, useDisclosure } from '@revelio/ui';

import type { TabItem } from '../../../next/CustomDash/hooks/useTabOrder';
import { useTabOrder } from '../../../next/CustomDash/hooks/useTabOrder';
import type { AnalyticsEntity } from '../../../next/CustomDash/utils/revelio-primary-filter-entity-switch';
import { AlertDialog } from '../../components/alert-dialog';
import { DragHandleIcon } from '../../ui/icons';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { PreferenceScope, UserPreferenceKey } from '../types';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.PageOrder,
  label: 'Analytics Tabs',
};

const SCOPE_ENTITY: Partial<Record<PreferenceScope, AnalyticsEntity>> = {
  [PreferenceScope.COMPANY]: 'company',
  [PreferenceScope.INDUSTRY]: 'industry',
  [PreferenceScope.GEOGRAPHY]: 'geography',
  [PreferenceScope.ROLE]: 'role',
  [PreferenceScope.SKILL]: 'skill',
  [PreferenceScope.ACTIVITY]: 'activity',
};

const SCOPE_LABEL: Partial<Record<PreferenceScope, string>> = {
  [PreferenceScope.COMPANY]: 'company',
  [PreferenceScope.INDUSTRY]: 'industry',
  [PreferenceScope.GEOGRAPHY]: 'geography',
  [PreferenceScope.ROLE]: 'role',
  [PreferenceScope.SKILL]: 'skill',
  [PreferenceScope.ACTIVITY]: 'activity',
};

function SortableTabRow({
  tab,
  position,
  disabled,
}: {
  tab: TabItem;
  position: number;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: 'none',
    cursor: disabled ? 'default' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={classNames(styles.tabRow, isDragging && styles.tabRowDragging)}
      data-testid={`analytics-tab-row-${tab.id}`}
    >
      {!disabled && (
        <DragHandleIcon
          size="0.625rem"
          aria-hidden
          className={styles.tabDragHandle}
        />
      )}
      <span className={styles.tabPosition}>{position}</span>
      <span className={styles.tabLabel}>{tab.label}</span>
      <span
        className={classNames(
          styles.tabKind,
          tab.kind === 'custom' && styles.tabKindCustom
        )}
      >
        {tab.kind === 'custom' ? 'Custom' : 'Built in'}
      </span>
    </div>
  );
}

export default function PageOrderEditor({
  affectedScope,
}: PreferenceEditorProps) {
  const confirm = useDisclosure();
  const entity = SCOPE_ENTITY[affectedScope] ?? 'company';
  const {
    orderedTabs,
    barTabs,
    previewMove,
    saveOrder,
    cancelPreview,
    resetOrder,
    isCustomOrder,
    canReorder,
  } = useTabOrder(entity);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (over && active.id !== over.id) {
      previewMove(String(active.id), String(over.id));
    }
  };
  const handleDragEnd = (_event: DragEndEvent) => saveOrder();

  // Tabs from this index down don't fit the bar and live in the Pages menu.
  const menuCutoff = barTabs.length;
  const hasMenuTabs = menuCutoff < orderedTabs.length;
  const firstDisplacedCustom = barTabs.find((t) => t.kind === 'custom');

  let headingAction: ReactNode;
  if (!canReorder) {
    headingAction = (
      <span className={styles.readOnlyHint}>Read-only while impersonating</span>
    );
  } else if (isCustomOrder) {
    headingAction = (
      <Button
        variant="link"
        size="sm"
        color="default"
        data-testid="reset-analytics-tab-order"
        onClick={confirm.onOpen}
      >
        Reset order
      </Button>
    );
  }

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Analytics Tabs"
        indicateDiff={false}
        tooltip={
          canReorder
            ? `Drag to reorder the ${SCOPE_LABEL[affectedScope] ?? ''} analytics tabs.`
            : undefined
        }
        action={headingAction}
      />

      <div className={styles.tabList} data-testid="analytics-tab-order-list">
        {orderedTabs.length === 0 && (
          <p className={styles.tabListEmpty}>
            No analytics tabs on this scope yet.
          </p>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={cancelPreview}
        >
          <SortableContext
            items={orderedTabs.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            {orderedTabs.map((tab, idx) => (
              <Fragment key={tab.id}>
                {hasMenuTabs && idx === menuCutoff && (
                  <div className={styles.tabCutoff}>
                    Tabs below here move into the Pages menu
                  </div>
                )}
                <SortableTabRow
                  tab={tab}
                  position={idx + 1}
                  disabled={!canReorder}
                />
              </Fragment>
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <AlertDialog
        skipDefaultDisclosure
        alertType="confirm"
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        headerText="Reset Analytics Tabs"
        bodyContent={
          <span>
            Tabs will return to their original order.
            {firstDisplacedCustom
              ? ` Custom pages such as “${firstDisplacedCustom.label}” stay, but move into the Pages menu.`
              : ' Custom pages stay, but move into the Pages menu.'}
          </span>
        }
        footerAcceptText="Reset"
        footerAcceptIsDestructive
        cancelColorScheme="gray"
        onAccept={() => resetOrder()}
      />
    </>
  );
}
